import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { EUR } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Download } from "lucide-react";

const METHOD_COLORS = ["#1C352D", "#E2725B", "#7C9885", "#C8A97E", "#556B70"];

export default function Reports() {
  // Default: parte da 1 gennaio 2025 (o 3 anni fa) fino a oggi per catturare
  // tutti i dati anche se l'anno corrente non ha ancora pagamenti registrati.
  const today = new Date().toISOString().slice(0, 10);
  const threeYearsAgo = `${new Date().getFullYear() - 2}-01-01`;
  const [since, setSince] = useState(threeYearsAgo);
  const [until, setUntil] = useState(today);
  const [donorType, setDonorType] = useState("all");
  const [method, setMethod] = useState("all");

  const { data } = useQuery({
    queryKey: ["reports", { since, until, donorType, method }],
    queryFn: async () => {
      const params = { since, until };
      if (donorType !== "all") params.donor_type = donorType;
      if (method !== "all") params.method = method;
      return (await api.get("/reports/summary", { params })).data;
    },
  });

  const byMethodArr = Object.entries(data?.by_method || {}).map(([k, v]) => ({ name: k, value: v }));

  function exportCsv() {
    const rows = [["Mese", "Totale €"]].concat((data?.by_month || []).map(r => [r.month, r.total]));
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `report_${since}_${until}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Report"
        subtitle="Analisi per periodo, metodo di pagamento e tipologia donatore."
        actions={
          <Button variant="outline" onClick={exportCsv} className="rounded-full" data-testid="report-export-csv">
            <Download size={14} className="mr-1.5"/> Esporta CSV
          </Button>
        }
      />

      <Card className="p-6 border border-stone-200 rounded-xl shadow-none mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Dal</Label>
            <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="mt-1.5"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Al</Label>
            <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1.5"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Tipo donatore</Label>
            <Select value={donorType} onValueChange={setDonorType}>
              <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="privato">Privati</SelectItem>
                <SelectItem value="chiesa">Chiese</SelectItem>
                <SelectItem value="azienda">Aziende</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Metodo</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="bonifico">Bonifico</SelectItem>
                <SelectItem value="contanti">Contanti</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Totale</div>
          <div className="text-3xl font-semibold mt-1">{EUR.format(data?.total || 0)}</div>
        </Card>
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Donazioni</div>
          <div className="text-3xl font-semibold mt-1">{data?.count || 0}</div>
        </Card>
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Donazione media</div>
          <div className="text-3xl font-semibold mt-1">{EUR.format(data?.average || 0)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium mb-3">Andamento per mese</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_month || []} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee7d9" vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6c655a" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 11, fill: "#6c655a" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v}/>
                <Tooltip formatter={(v) => EUR.format(v)} contentStyle={{ background: "#fff", border: "1px solid #e7e2d3", borderRadius: 8, fontSize: 12 }}/>
                <Bar dataKey="total" fill="#1C352D" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium mb-3">Distribuzione per metodo</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byMethodArr} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                  {byMethodArr.map((_, i) => <Cell key={i} fill={METHOD_COLORS[i % METHOD_COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={(v) => EUR.format(v)}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {byMethodArr.map((m, i) => (
              <div key={m.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: METHOD_COLORS[i % METHOD_COLORS.length] }} />
                  <span className="capitalize">{m.name}</span>
                </div>
                <span className="font-semibold">{EUR.format(m.value)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
